import { useEffect, useState } from 'react'
import Navigation from '@/components/Navigation'
import heroImage from '@assets/DSC_8101_copy_1768257933271.jpg'
import brothersImage from '@assets/DSC_8033_copy_1768255423551.jpg'
import fightingImage from '@assets/DSC_8087_copy_1768257845930.jpg'
import scriptGunImage from '@assets/DSC_8008_copy_1768258629903.jpg'
import filmStrip1 from '@assets/DSC_7911_copy_1768259584984.jpg'
import filmStrip2 from '@assets/DSC_7916_1768259584986.jpg'
import filmStrip3 from '@assets/DSC_7978_copy_1768259592827.jpg'
import filmStrip4 from '@assets/DSC_7989_1768259592828.jpg'
import finalImage from '@assets/DSC_8129_copy2_(1)_(1)_1768540435689.jpg'
import { trackEvent } from '@/lib/analytics'

const HomePage = () => {
  const [scrollY, setScrollY] = useState(0)
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null)
  const allImages = [
    brothersImage, fightingImage, scriptGunImage,
    filmStrip1, filmStrip2, filmStrip3, filmStrip4,
    finalImage
  ]

  const openGallery = (imageIndex: number, galleryId: string) => {
    trackEvent('film_click', {
      site: 'the-monsters-we-make',
      film: 'the-monsters-we-make',
      action: 'gallery_open',
      placement: 'home_gallery',
      provider: galleryId,
    })
    setSelectedImageIndex(imageIndex)
  }
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  
  const heroHeight = typeof window !== 'undefined' ? window.innerHeight : 800
  const scrollProgress = Math.min(scrollY / heroHeight, 1)
  const translateY = scrollY * 0.5
  const opacity = 1 - Math.max(0, scrollProgress - 0.3) * 2
  
  return (
    <>
      <Navigation />
      
      <main className="min-h-screen bg-[#0a0a0a]">
        <section className="relative min-h-screen flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-cover bg-left bg-no-repeat"
            style={{ backgroundImage: `url(${heroImage})` }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0a0a0a]"></div>
          
          <div 
            className="relative z-10 text-center px-6 max-w-5xl mx-auto"
            style={{ 
              transform: `translateY(${translateY}px)`,
              opacity: Math.max(opacity, 0)
            }}
          >
            <h1 className="font-bebas text-6xl md:text-8xl lg:text-9xl text-red-600 tracking-wider mb-6 text-shadow-brutal">
              THE MONSTERS WE MAKE
            </h1>
            
            <p className="font-poppins text-xl md:text-2xl text-neutral-300 font-light mb-8 max-w-3xl mx-auto">
              Some families break you. Some make you fight back.
            </p>
            <p className="font-bebas text-lg md:text-xl text-red-600 tracking-wider mb-12">
              Based on the true story of Jared and André Van Driessche
            </p>

            <div className="w-24 h-[2px] bg-red-600 mx-auto"></div>
          </div>
        </section>

        <section className="py-8 px-4 md:px-6">
          <div
            className="relative max-w-6xl mx-auto min-h-[68vh] md:min-h-[78vh] overflow-hidden cursor-pointer group"
            onClick={() => openGallery(1, 'fighting_image')}
          >
            <img
              src={fightingImage}
              alt="Jared and André ready to fight"
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-black/20"></div>
            <div className="absolute inset-x-0 bottom-0 p-6 md:p-12">
              <p className="font-bebas text-5xl md:text-7xl lg:text-8xl text-white tracking-wide leading-[0.95] max-w-4xl">
                A dead falcon sits in a freezer
              </p>
              <p className="font-poppins text-lg md:text-2xl text-red-500 mt-5 max-w-3xl">
                A Vivaldi concerto plays over a highway at sunrise
              </p>
            </div>
          </div>
        </section>

        <section className="py-4 px-4 md:px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-4 md:gap-6">
            <div
              className="relative min-h-[60vh] overflow-hidden cursor-pointer group"
              onClick={() => openGallery(0, 'brothers_image')}
            >
              <img
                src={brothersImage}
                alt="Jared and André face to face"
                className="absolute inset-0 w-full h-full object-cover object-center md:object-[center_25%] grayscale group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent"></div>
              <p className="absolute inset-x-0 bottom-0 p-6 md:p-8 font-bebas text-4xl md:text-5xl text-white tracking-wide leading-none">
                A stripper<br />A mask<br /><span className="text-red-600">A therapist waits</span>
              </p>
            </div>

            <div
              className="relative min-h-[60vh] overflow-hidden cursor-pointer group"
              onClick={() => openGallery(2, 'script_gun_image')}
            >
              <img
                src={scriptGunImage}
                alt="A fragmented memory from The Monsters We Make"
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <p className="font-bebas text-4xl md:text-5xl text-white tracking-wide leading-none">
                  A newborn cries in the arms of his father who might as well be a stranger
                </p>
                <p className="font-poppins text-lg md:text-2xl text-red-500 mt-5">
                  A father dying inside a memory
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter popup trigger */}
        <section className="py-14 px-6 bg-black border-t border-b border-neutral-800">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-bebas text-3xl md:text-4xl text-white tracking-wide mb-3">
              DON'T MISS SEEING THE MONSTERS WE MAKE.
            </h2>
            <p className="font-poppins text-neutral-400 text-base mb-7">
              Get BTS updates &amp; Early Access
            </p>
            <a
              data-formkit-toggle="712c011a78"
              href="https://filmabee-studios.kit.com/712c011a78"
              onClick={() => trackEvent('newsletter_click', { site: 'the-monsters-we-make', film: 'the-monsters-we-make', placement: 'home', provider: 'kit' })}
              className="inline-block font-bebas text-xl tracking-widest px-10 py-3 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors cursor-pointer"
            >
              STAY UPDATED
            </a>
          </div>
        </section>

        <section className="py-10 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-neutral-300 space-y-6 text-sm md:text-base leading-relaxed">
              <p>
                Tupac. Headphones. Rage simmering. Loops running through their past. Their father, at every turn, every reflection, every broken mirror.
              </p>
              <p>
                The Monsters We Make is not a film about trauma. It&apos;s a film about the moment two brothers, Jared and André, stop letting trauma choose for them.
              </p>
              <p>
                A conversation about masculinity, inherited shame.
              </p>
              <p>
                The specific, sacred, and unglamorous work of becoming a better father than the one you were given.
              </p>
              <p>
                Some families break you. Some make you fight back.
              </p>
              <p className="font-semibold text-white">
                Both are true.
              </p>
            </div>
          </div>
        </section>

        <section className="py-8 overflow-hidden">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto px-3">
            <div className="aspect-[3/4] w-full overflow-hidden rounded-sm cursor-pointer" onClick={() => openGallery(3, 'film_strip_1')}>
              <img src={filmStrip1} alt="Portrait 1" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300" />
            </div>
            <div className="aspect-[3/4] w-full overflow-hidden rounded-sm cursor-pointer" onClick={() => openGallery(4, 'film_strip_2')}>
              <img src={filmStrip2} alt="Portrait 2" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300" />
            </div>
            <div className="aspect-[3/4] w-full overflow-hidden rounded-sm cursor-pointer" onClick={() => openGallery(5, 'film_strip_3')}>
              <img src={filmStrip3} alt="Portrait 3" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300" />
            </div>
            <div className="aspect-[3/4] w-full overflow-hidden rounded-sm cursor-pointer" onClick={() => openGallery(6, 'film_strip_4')}>
              <img src={filmStrip4} alt="Portrait 4" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-300" />
            </div>
          </div>
        </section>

        {selectedImageIndex !== null && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImageIndex(null)}
          >
            <button 
              className="absolute left-4 md:left-8 text-white text-5xl font-light hover:text-red-600 transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); setSelectedImageIndex((selectedImageIndex - 1 + allImages.length) % allImages.length); }}
            >
              ‹
            </button>
            
            <img 
              src={allImages[selectedImageIndex]} 
              alt="Enlarged photo" 
              className="max-h-[90vh] max-w-[90vw] object-contain rounded-sm"
              onClick={(e) => e.stopPropagation()}
            />
            
            <button 
              className="absolute right-4 md:right-8 text-white text-5xl font-light hover:text-red-600 transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); setSelectedImageIndex((selectedImageIndex + 1) % allImages.length); }}
            >
              ›
            </button>
            
            <button 
              className="absolute top-6 right-6 text-white text-4xl font-light hover:text-red-600 transition-colors"
              onClick={() => setSelectedImageIndex(null)}
            >
              ×
            </button>
          </div>
        )}

        <section className="py-10 px-6">
          <div className="max-w-xl mx-auto font-mono text-neutral-300 text-base leading-relaxed">
            <p className="text-center text-red-600 font-bold uppercase tracking-wider text-lg">ANDRÉ</p>
            <p className="mb-4">
              On the flight, I sat next to a Japanese girl...
            </p>
            <p className="mb-4">
              Over there they grow these melons in square containers, easy to stack and store, hundreds of dollars each. She let me try a piece.
            </p>
            <p className="mb-4">
              But that's not the point... They take a young melon and put it in this square container. So while it develops, the square shapes the melon.
            </p>
            <p className="mb-4">
              It tastes like a melon, but it's a square fucking melon. It's a mind fuck...
            </p>
            <p>
              You know what the Japanese did to those melons, he did to us. I wanted to say I am...
            </p>
          </div>
        </section>

        <section className="py-10 px-6 bg-black/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-bebas text-4xl md:text-5xl text-red-600 tracking-wide mb-8 lg:text-center">
              BE PART OF SOMETHING REAL
            </h2>

            <div className="text-neutral-300 font-poppins text-lg leading-relaxed">
              <div className="lg:flex lg:gap-12 lg:items-start mb-8">
                <div className="lg:flex-1">
                  <p className="mb-6">
                    We are putting together an incredible team of professionals. Including a marketing team because at Filmabee Studios marketing is not an afterthought.
                  </p>
                  <p>
                    And if you believe in cinema with social impact and long-term value, we'd love to connect.
                  </p>
                </div>
                <div className="mt-6 lg:mt-0 lg:w-[300px] lg:flex-shrink-0 flex items-center justify-center">
                  <img src="/the-monsters-we-make/filmabee-logo-white.png" alt="Filmabee Studios" className="w-full" />
                </div>
              </div>

              <div className="text-center mt-8 mb-12">
                <p className="font-bebas text-4xl md:text-5xl tracking-wide text-white">
                  "You're not joking. You really are<br />trying this 'better human' shit!?"
                </p>
                <p className="text-red-600 font-bebas text-3xl tracking-wide mt-2">
                  — JARED
                </p>
              </div>

              <div className="flex justify-center mb-12">
                <img src={finalImage} alt="Jared and André" className="w-full max-w-xl rounded-sm cursor-pointer hover:opacity-90 transition-opacity" onClick={() => openGallery(7, 'final_image')} />
              </div>

            </div>

            </div>
        </section>

        {/* Get In Touch CTA */}
        <section className="py-20 px-6 bg-black/50">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-24 h-[2px] bg-red-600 mx-auto mb-10"></div>
            <h2 className="font-bebas text-4xl md:text-5xl text-white tracking-wide mb-4">
              INTERESTED IN BEING PART OF THE JOURNEY?
            </h2>
            <p className="font-poppins text-neutral-400 text-lg mb-10">
              Let's talk.
            </p>
            <a
              href="/the-monsters-we-make/get-in-touch"
              onClick={() => trackEvent('contact_click', {
                site: 'the-monsters-we-make',
                film: 'the-monsters-we-make',
                placement: 'home_cta',
                provider: 'internal',
              })}
              className="inline-block font-bebas text-2xl tracking-widest px-12 py-4 bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              GET IN TOUCH
            </a>
          </div>
        </section>

        {/* Newsletter popup trigger */}
        <section className="py-12 px-6 bg-black border-t border-neutral-800">
          <div className="max-w-2xl mx-auto text-center">
            <p className="font-poppins text-neutral-400 text-base mb-6">
              Want to follow this film as it develops?
            </p>
            <a
              data-formkit-toggle="712c011a78"
              href="https://filmabee-studios.kit.com/712c011a78"
              onClick={() => trackEvent('newsletter_click', {
                site: 'the-monsters-we-make',
                film: 'the-monsters-we-make',
                placement: 'bottom_newsletter',
                provider: 'kit',
              })}
              className="inline-block font-bebas text-xl tracking-widest px-10 py-3 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors cursor-pointer"
            >
              STAY UPDATED
            </a>
          </div>
        </section>

        <div className="flex justify-center py-8">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 text-neutral-400 hover:text-red-600 transition-colors font-poppins text-sm tracking-wide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 15l-6-6-6 6"/>
            </svg>
            Back to Top
          </button>
        </div>

        <footer className="py-8 px-6 border-t border-neutral-800">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-poppins text-neutral-500 text-sm">
              © 2026 Filmabee Studios. All rights reserved.
            </p>
            <p className="font-poppins text-neutral-500 text-sm">
              Photography by the amazing Tzvetan Tzenov
            </p>
            <a 
              href="/" 
              className="font-poppins text-neutral-400 hover:text-red-600 transition-colors text-sm"
            >
              Filmabee Studios
            </a>
          </div>
        </footer>
      </main>
    </>
  )
}

export default HomePage
